/**
 * Top-bar notification bell with a summary dropdown.
 *
 * Clicking the bell opens a small panel of the most recent notifications
 * (it no longer navigates straight to the page). Clicking a notification —
 * or "View all" — goes to the full notifications page. Click-outside / Escape
 * closes the panel.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";

import { api, type Paginated } from "@/lib/api";
import { useUnreadNotifications } from "@/lib/hooks";
import { formatRelative } from "@/lib/format";
import type { Notification } from "@/lib/types";
import { cn } from "@/lib/utils";

const MAX_SUMMARY = 6;

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const unread = useUnreadNotifications();
  const unreadCount = unread.data?.length ?? 0;

  // Recent notifications (read + unread), fetched only while the panel is open.
  const recent = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: async () => {
      const resp = await api.get<Notification[] | Paginated<Notification>>(
        "/notifications/inbox/",
      );
      const rows = Array.isArray(resp) ? resp : resp.results;
      return rows.slice(0, MAX_SUMMARY);
    },
    enabled: open,
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      api.post(`/notifications/inbox/${id}/mark-read/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function openNotification(n: Notification) {
    setOpen(false);
    if (!n.read_at) markRead.mutate(n.id);
    navigate("/notifications");
  }

  const rows = recent.data ?? [];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-btn text-slate hover:bg-slate-soft hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/20"
        aria-label={
          unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"
        }
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={18} strokeWidth={1.5} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-card border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-navy">Notifications</p>
            {unreadCount > 0 && (
              <span className="text-xs text-slate">{unreadCount} unread</span>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {recent.isLoading ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">
                You're all caught up.
              </p>
            ) : (
              <ul>
                {rows.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => openNotification(n)}
                      className="flex w-full items-start gap-2.5 border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-soft focus-visible:outline-none focus-visible:bg-slate-soft"
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                          n.read_at ? "bg-transparent" : "bg-blue",
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            "block truncate text-sm",
                            n.read_at ? "text-slate-700" : "font-medium text-navy",
                          )}
                        >
                          {n.subject}
                        </span>
                        {n.body && (
                          <span className="mt-0.5 block truncate text-xs text-slate-400">
                            {n.body}
                          </span>
                        )}
                        <span className="mt-0.5 block text-[11px] text-slate-400">
                          {formatRelative(n.created_at)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="block w-full border-t border-slate-100 bg-bg px-4 py-2.5 text-center text-sm font-medium text-blue hover:bg-slate-soft"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
